import * as PIXI from 'pixi.js';

import { getGlobalFontManager } from '../../text/FontManager';
import { Config } from '../../../video-data/config';

import { TypedParentMixin } from '../../parentMixin';
import { TypedUpdateFlowMixin } from '../../updateFlowMixin';
import { TypedSerializableMixin } from '../../serializableMixin';

import {
    AlphaAttribute,
    BooleanAttribute,
    ColorAttribute,
    FontAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize
} from '../../attributes';
import type {
    CanvasTextStyleRendererSettings
} from '../renderer/CanvasTextStyle';
import {
    CanvasTextStyleRenderer
} from '../renderer/CanvasTextStyle';
import { ReadingDirection, ValueMapMode } from '../../../enums';
import { int2rgba } from '../../../libs/videoData';
import type { CanvasTextDynamicAnimationRendererSettings } from '../renderer/CanvasTextDynamicAnimationBase';
import type { DynamicGraphicAsset, DynamicGraphicType, GetDynamicGraphicType } from '../types';
import type { RawTextAssetStyleData } from '../../../video-data/types';
import { getAllTemplateAttributes, getTemplateValueAttribute, isTemplateReference } from '../../attributes/index';
import type { CopyPasteData, CopyPasteStyle } from '../../asset/BaseAsset';
import { findTemplateIndexErrors, getStylePropertiesToCopy } from '../../asset/utils';
import { ROUNDING_PRECISION } from '../metrics/CanvasTextMetrics';
import { roundToPrecision } from '../../../libs/frameUtils';
import type { AssetTemplateReferenceMap } from '../../../types';
import { CanvasTextAssetDynamicBox } from './CanvasTextAssetDynamicBox';
import { CanvasTextAssetDynamicAnimation } from './CanvasTextAssetDynamicAnimation';
import { CanvasTextAssetDynamicCircle } from './CanvasTextAssetDynamicCircle';
import { CanvasTextAssetDynamicBoxBorder } from './CanvasTextAssetDynamicBoxBorder';

/**
 * CanvasTextAssetStyle is the Luminary equivalent of CanvasTextStyle and provides a bridge between Luminary
 * and the CanvasText library.
 */
export class CanvasTextAssetStyle extends TypedUpdateFlowMixin(TypedParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter))) implements CopyPasteStyle {
    override _classType = 'CanvasTextAssetStyle';

    #destroyed = false;
    protected _requiresRedraw = false;

    @FontAttribute()
    fontUrl = Config.DEFAULT_FONT_URL;
    @StringAttribute({ choices: Object.values(Config.READING_DIRECTION), serialize: false })
    readingDirection = ReadingDirection.LTR;

    // FontSize is not serialized because it is a transient property that is calculated by the TextAsset
    @NumberAttribute({ serialize: false })
    fontSize = 26;

    // Leading is not serialized because it is a transient property that is calculated by the TextAsset
    @NumberAttribute({ serialize: false })
    leading = 0.2;
    @ColorAttribute()
    fill: number = 0;
    // Specifies if this style should render the text within the highlighted areas
    @BooleanAttribute()
    renderHighlighted = false;
    // Specifies if the normal style should use the highlighted color instead of the normal color to render the highlighted areas
    @BooleanAttribute()
    renderHighlightedColor = false;
    @BooleanAttribute()
    shadow = false;
    @NumberAttribute({ min: 0, max: 360 })
    shadowAngle = 1;
    @NumberAttribute({ min: 0, max: Infinity })
    shadowBlur = 10;
    @AlphaAttribute()
    shadowAlpha = 0.5;
    @ColorAttribute()
    shadowColor: number = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    shadowDistance = 2;

    #referenceToOutputMultiplier = 1;

    #graphics: DynamicGraphicAsset[] = [];

    #animationConfigurations: CanvasTextAssetDynamicAnimation[] = [];

    constructor(settings) {
        super(settings);

        this.#referenceToOutputMultiplier = settings.referenceToOutputMultiplier;
    }

    get destroyed() {
        return this.#destroyed;
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.#referenceToOutputMultiplier;
    }

    get animationConfigurations() {
        return this.#animationConfigurations;
    }

    _animationConfigurationsCustomLoad(properties: Record<string, any>[]) {
        if (!properties) {
            return;
        }
        this.#animationConfigurations.splice(properties.length);
        properties.forEach((data, index) => {
            const animation = this.#animationConfigurations[index] ?? this.addAnimation();
            animation.load(data);
        });
    }

    /**
     * Adds a CanvasTextAssetDynamicAnimation to the `animation` list.
     * The added configurator can be configured to animate any attribute on the asset.
     */
    addAnimation() {
        const animation = new CanvasTextAssetDynamicAnimation({
            parent: this,
            updateParentOnUpdate: true,
        });

        // do not mutate, we need to create a new reference for React
        this.#animationConfigurations = [...this.#animationConfigurations, animation];
        this.requiresUpdate = true;

        return animation;
    }

    removeAnimation(index: number) {
        // do not mutate, we need to create a new reference for React
        const animation = [...this.#animationConfigurations];
        animation.splice(index, 1);
        this.#animationConfigurations = animation;
        this.requiresUpdate = true;
    }

    get graphics() {
        return this.#graphics;
    }

    get colorGraphics() {
        return this.#graphics.filter(graphic => !graphic.asMask);
    }

    get alphaGraphics() {
        return this.#graphics.filter(graphic => graphic.asMask);
    }

    #createGraphic<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T>;
    #createGraphic(type: DynamicGraphicType): DynamicGraphicAsset {
        let graphic: DynamicGraphicAsset;
        const settings = {
            parent: this,
            autoUpdate: true,
            updateParentOnUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
        };
        switch (type) {
            case Config.BOX_BORDER_GRAPHIC: graphic = new CanvasTextAssetDynamicBoxBorder(settings); break;
            case Config.BOX_GRAPHIC: graphic = new CanvasTextAssetDynamicBox(settings); break;
            case Config.CIRCLE_GRAPHIC: graphic = new CanvasTextAssetDynamicCircle(settings); break;
            default: throw new Error(`Invalid type ${type} for graphic`);
        }
        return graphic;
    }

    _graphicsCustomLoad(properties: RawTextAssetStyleData[], settings = { skipTemplateAttributes: false }) {
        this.#graphics.splice(properties.length);
        properties.forEach((dataObj, index) => {
            const graphic = this.#graphics[index] ?? this.addGraphicByType(dataObj.type);
            graphic.load(dataObj, settings);
        });
    }

    addGraphicByType<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T> {
        const graphic = this.#createGraphic(type);
        // do not mutate, we need to create a new reference for React
        this.#graphics = [...this.#graphics, graphic];
        this.requiresUpdate = true;

        return graphic;
    }

    addColorGraphicByType<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T> {
        const graphic = this.addGraphicByType(type);
        graphic.asMask = false;
        return graphic;
    }

    addAlphaGraphicByType<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T> {
        const graphic = this.addGraphicByType(type);
        graphic.asMask = true;
        return graphic;
    }

    removeGraphic(index: number) {
        // do not mutate, we need to create a new reference for React
        const graphics = [...this.#graphics];
        graphics.splice(index, 1);
        this.#graphics = graphics;
        this.requiresUpdate = true;
    }

    getCanvasRendererSettings(): CanvasTextStyleRendererSettings {
        // convert asset color to canvas color
        const fill = int2rgba(this.fill as number, 1.0);
        const shadowColor = int2rgba(this.shadowColor, this.shadowAlpha);

        // convert asset graphics to canvas graphics
        const graphics = this.#graphics
            .filter(graphic => graphic.enabled)
            .map(graphic => graphic.getCanvasRendererSettings());

        // convert asset animations to canvas animations
        const animationConfigurations = this.#animationConfigurations
            .filter(animationConfiguration => animationConfiguration.enabled)
            .map(animationConfiguration =>
                animationConfiguration.getCanvasRendererSettings() as CanvasTextDynamicAnimationRendererSettings);

        const fontManager = getGlobalFontManager();
        const fontLoader = fontManager.getFontBySourceIfLoaded(this.fontUrl);
        // Font loader can be null while loading font, use a default, as a family name is required in the bridge data.
        const fontFamily = fontLoader?.familyName ?? 'Arial';

        return {
            fill,
            fontFamily,
            fontSize: roundToPrecision(this.fontSize * this.referenceToOutputMultiplier, ROUNDING_PRECISION),
            lineHeight: 1.0 + (this.leading / this.fontSize),
            readingDirection: this.readingDirection,
            renderHighlighted: this.renderHighlighted,
            renderHighlightedColor: this.renderHighlightedColor,
            shadow: this.shadow,
            shadowAngle: this.shadowAngle * this.referenceToOutputMultiplier,
            shadowBlur: this.shadowBlur * this.referenceToOutputMultiplier,
            shadowColor: shadowColor,
            shadowDistance: this.shadowDistance * this.referenceToOutputMultiplier,
            graphics,
            animationConfigurations,
        };
    }

    get stylePropertiesToCopy(): string[] {
        return [
            'fontUrl',
            'fill',
            'renderHighlighted',
            'shadow',
            'shadowAngle',
            'shadowBlur',
            'shadowAlpha',
            'shadowColor',
            'shadowDistance',
            'animationConfigurations',
            'graphics',
        ];
    }

    copyStyleProperties(): CopyPasteData {
        return getStylePropertiesToCopy(this);
    }

    pasteStyleProperties(settings: CopyPasteData): void {
        const templateIndexErrorsList: string[] = [];

        findTemplateIndexErrors(this.template, '', settings._templateReferenceMap, templateIndexErrorsList);

        if (templateIndexErrorsList.length > 0) {
            throw new Error(templateIndexErrorsList.join(' '));
        }

        // Only get whitelisted properties
        const filteredSettings = Object.fromEntries(Object.entries(settings).filter(([key, _value]) => {
            if (!this.stylePropertiesToCopy.includes(key)) {
                return false;
            }
            return true;
        }));

        this.load(filteredSettings);
    }

    override get propertiesToSerialize() {
        return getAttributesToSerialize(this).concat([
            'animationConfigurations',
            'graphics',
        ]);
    }

    get animatableProperties(): string[] {
        return CanvasTextStyleRenderer.animatableProperties;
    }


    /** The list of geometry properties that can be animated during draw. */
    get animatableGeometricProperties(): string[] {
        return CanvasTextStyleRenderer.animatableGeometricProperties;
    }

    /**
     * Builds a map of all values referencing template value in the style
     */
    buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        return this._buildValueMap(ValueMapMode.GET_REFERENCE_VALUES);
    }

    /**
     * Builds value map of all attributes in style and its graphics.
     * Prefix can be used to prepend a prefix to all paths.
     * Ex.: for normalStyle, it's useful to prepend "normalStyle" to all paths.
     * @internal
     */
    _buildValueMap(valueMapMode = ValueMapMode.GET_REFERENCE_VALUES, prefix: string = null) {
        const result = {};

        // Add `.` after prefix, if present.
        if (prefix) {
            prefix = `${prefix}.`;
        }
        else {
            prefix = '';
        }

        const attributes = getAllTemplateAttributes(this, valueMapMode);

        attributes.forEach(attr => {
            const attrKey = `${prefix}${attr}`;

            if (isTemplateReference(this[getTemplateValueAttribute(attr)])) {
                if (valueMapMode === ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE) {
                    result[attrKey] = this[attr];
                }
                else {
                    result[attrKey] = this[getTemplateValueAttribute(attr)].replace('themeRef_', '');
                }
            }
            else if (valueMapMode !== ValueMapMode.GET_REFERENCE_VALUES) {
                result[attrKey] = this[attr];
            }
        });

        this.graphics.forEach((graphic, index) => {
            if (graphic.asMask) {
                // skip alpha graphics because they're not visible
                return;
            }
            const attrKeyPrefix = `${prefix}graphics.${index}.`;
            // TODO: we should use getTemplateAttributes here once we have a way to get inherited attributes
            const templateAttributes = graphic.propertiesToSerialize
                .filter(attr => graphic[getTemplateValueAttribute(attr)] !== undefined);
            templateAttributes.forEach(attr => {
                const attrKey = `${attrKeyPrefix}${attr}`;
                if (isTemplateReference(graphic[getTemplateValueAttribute(attr)])) {
                    if (valueMapMode === ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE) {
                        result[attrKey] = graphic[attr];
                    }
                    else {
                        result[attrKey] = graphic[getTemplateValueAttribute(attr)].replace('themeRef_', '');
                    }
                }
                else if (valueMapMode !== ValueMapMode.GET_REFERENCE_VALUES) {
                    result[attrKey] = graphic[getTemplateValueAttribute(attr)];
                }
            });
        });
        return result;
    }

    removeDynamicStyles() {
        this.#animationConfigurations = [];
        this.#graphics = [];
        this.requiresUpdate = true;
    }

    override get readonlyProperties() {
        return [];
    }

    loadTextAssetStyle(settings) {
        if (settings.textColor !== undefined) {
            this.fill = settings.textColor;
        }
        if (settings.leading !== undefined) {
            this.leading = settings.leading;
        }
        if (settings.fontSize !== undefined) {
            this.fontSize = settings.fontSize;
        }
        if (settings.fontUrl !== undefined) {
            this.fontUrl = settings.fontUrl;
        }
        if (settings.readingDirection !== undefined) {
            this.readingDirection = settings.readingDirection;
        }
        if (settings.textShadow !== undefined) {
            this.shadow = settings.textShadow;
        }
        if (settings.textShadowAngle !== undefined) {
            this.shadowAngle = settings.textShadowAngle;
        }
        if (settings.textShadowBlur !== undefined) {
            this.shadowBlur = settings.textShadowBlur;
        }
        if (settings.textShadowColor !== undefined) {
            this.shadowColor = settings.textShadowColor;
        }
        if (settings.textShadowDistance !== undefined) {
            this.shadowDistance = settings.textShadowDistance;
        }

        if (settings.colorGraphics !== undefined) {
            settings.colorGraphics.forEach(colorGraphicSettings => {
                const graphic = this.addGraphicByType(colorGraphicSettings.graphic);
                graphic.loadTextAssetStyle(colorGraphicSettings);
                graphic.asMask = false;
            });
        }

        if (settings.alphaGraphics !== undefined) {
            settings.alphaGraphics.forEach(alphaGraphicSettings => {
                const graphic = this.addGraphicByType(alphaGraphicSettings.graphic);
                graphic.loadTextAssetStyle(alphaGraphicSettings);
                graphic.asMask = true;
            });
        }
    }
}
